import { z } from "zod";
import { IsoDate } from "./primitives.js";
/**
 * @fileoverview Which index a company was in, read out of the holdings of the fund that
 * tracks it.
 *
 * "Is this company in the S&P 500" is answered as "did the fund tracking the S&P 500
 * hold it in its last NPORT-P". That is an inference from a filing, so the holdings'
 * as-of date and the name-resolution rate travel with the answer.
 */
/**
 * The funds Mosaic reads, one per index.
 *
 * Each is a single-fund trust, so its CIK alone identifies its NPORT-P; a fund in a
 * multi-series trust would also need its series id.
 */
export const INDEX_FUNDS = [
    {
        key: "sp500",
        label: "S&P 500",
        ticker: "SPY",
        cik: "0000884394",
        fund: "SPDR S&P 500 ETF Trust",
    },
    {
        key: "nasdaq100",
        label: "Nasdaq-100",
        ticker: "QQQ",
        cik: "0001067839",
        fund: "Invesco QQQ Trust, Series 1",
    },
];
/** An index Mosaic reads membership of, through the holdings its tracking fund files. */
export const IndexKey = z.enum(["sp500", "nasdaq100"]);
/** A company's place in one index, as of that index's newest holdings on record. */
export const IndexMembership = z.object({
    index: IndexKey,
    label: z.string(),
    /** The date the fund's holdings are stated as of — NOT when they were filed. */
    asOf: IsoDate,
    /** Days between that date and today. */
    ageDays: z.number().int().nonnegative(),
    /** Share of the fund, as a decimal string, when the company was held. */
    weightPct: z.string().nullable(),
});
/**
 * The percentage of SPY's holdings the canonical name form resolves, as measured on its
 * filing (pinned in `indexMembership.test.ts`). Carried in the response rather than
 * written into the UI.
 */
export const HOLDING_NAME_RESOLUTION_PCT = 95.2;
/**
 * What Mosaic can say about one company's index membership.
 *
 * `not_matched` is not "not in the index". A holding names no CIK and no ticker, so the
 * match is on a canonical name form, and some names do not match (EDGAR's
 * `HORTON D R INC` against a fund's `DR Horton Inc`). A held company whose name did not
 * match is indistinguishable from an absent one, so the status says "not among the
 * holdings read" and carries the resolution rate.
 */
export const IndexMembershipResult = z.union([
    z.object({ status: z.literal("held"), memberships: z.array(IndexMembership) }),
    z.object({
        status: z.literal("not_matched"),
        checked: z.array(IndexKey),
        asOf: IsoDate,
        /** The percentage of holdings this method resolves at all. */
        resolutionPct: z.number(),
    }),
    /** No name to match — a SpinCo before the filings name it. */
    z.object({ status: z.literal("unnamed") }),
    /** No fund holdings ingested yet, which is not a statement about any company. */
    z.object({ status: z.literal("no_holdings") }),
]);
//# sourceMappingURL=index_membership.js.map